import React, { useMemo } from "react";
import {
  EuiButtonGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSelect,
  EuiText,
} from "@elastic/eui";
import {
  OplItem,
  OplItemType,
  OplMeeting,
  isOplItemClosed,
} from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import {
  GROUP_ORDER,
  OplBoardView,
  OplGroupKey,
  fmtDate,
  groupOf,
  isOverdueIn,
  isVisible,
  lastLogEntry,
  ownerShort,
  peopleOf,
  statusLabel,
  typeLabel,
  weekBoundary,
} from "./format";

/**
 * The middle column: every visible point, grouped in the order the round works
 * through them.
 *
 * Rows are a CSS grid rather than a table so the log preview can ellipsize
 * against `minmax(0, 1fr)` while the fixed columns keep their alignment across
 * groups. Each row is a real `<button>` - the whole row is the hit target, and
 * it stays keyboard reachable.
 */

const i18n = "anketa.opsanio.opl";

export interface PointListProps {
  items: OplItem[];
  meetings: OplMeeting[];
  round: OplMeeting | null;
  view: OplBoardView;
  onViewChange: (view: OplBoardView) => void;
  typeFilter: OplItemType | "all";
  onTypeFilterChange: (value: OplItemType | "all") => void;
  ownerFilter: string;
  onOwnerFilterChange: (value: string) => void;
  selectedId: string | null;
  onSelect: (item: OplItem) => void;
}

export const PointList = ({
  items,
  meetings,
  round,
  view,
  onViewChange,
  typeFilter,
  onTypeFilterChange,
  ownerFilter,
  onOwnerFilterChange,
  selectedId,
  onSelect,
}: PointListProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const t = (k: string, d: string, values?: Record<string, unknown>) =>
    translate(`${i18n}.${k}`, { defaultMessage: d, values });

  const sequence = round?.sequence ?? 0;

  const { groups, hiddenCount } = useMemo(() => {
    const visible = items.filter((i) => isVisible(i, meetings, sequence, view));
    const byGroup: Record<OplGroupKey, OplItem[]> = {
      overdue: [],
      thisWeek: [],
      new: [],
      rest: [],
      closed: [],
    };
    visible
      .filter((i) => typeFilter === "all" || i.type === typeFilter)
      .filter((i) => ownerFilter === "all" || i.owner === ownerFilter)
      .sort((a, b) => a.key.localeCompare(b.key))
      .forEach((i) => byGroup[groupOf(i, round)].push(i));
    return { groups: byGroup, hiddenCount: items.length - visible.length };
  }, [items, meetings, sequence, view, typeFilter, ownerFilter, round]);

  const owners = useMemo(() => {
    const fromRound = peopleOf(round).map((p) => ({
      value: p.email,
      text: p.label,
    }));
    // Owners of existing points who are not in this round's attendee list still
    // need to be filterable - people leave the Stammbesetzung, their points stay.
    const extra = [...new Set(items.map((i) => i.owner).filter(Boolean) as string[])]
      .filter((email) => !fromRound.some((o) => o.value === email))
      .map((email) => ({ value: email, text: email }));
    return [
      { value: "all", text: t("filter.all", "alle") },
      ...fromRound,
      ...extra,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, items, translate]);

  const groupMeta: Record<OplGroupKey, { label: string; hint: string; mod: string }> = {
    overdue: {
      label: t("group.overdue", "Überfällig"),
      hint: t("group.overdue.hint", "zuerst behandeln"),
      mod: " oplGroup__label--overdue",
    },
    thisWeek: {
      label: t("group.thisWeek", "Diese Woche fällig"),
      hint: weekBoundary(round),
      mod: "",
    },
    new: {
      label: t("group.new", "Neu eingegangen"),
      hint: round?.key ?? "",
      mod: "",
    },
    rest: {
      label: t("group.rest", "Übrige"),
      hint: t("group.rest.hint", "nur auf Zuruf"),
      mod: "",
    },
    closed: {
      label: t("group.closed", "Geschlossen"),
      hint: t("group.closed.hint", "letztmalig sichtbar, danach ausgeblendet"),
      mod: " oplGroup__label--closed",
    },
  };

  return (
    <main className="oplBoard__main">
      {/* --- filters --- */}
      <EuiFlexGroup
        gutterSize="s"
        alignItems="center"
        wrap
        responsive={false}
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid var(--opl-line-soft)",
          flexGrow: 0,
        }}
      >
        <EuiFlexItem grow={false}>
          <EuiButtonGroup
            legend={t("filter.type", "Typ")}
            buttonSize="compressed"
            options={[
              { id: "all", label: t("filter.all", "alle") },
              { id: "I", label: "I" },
              { id: "A", label: "A" },
              { id: "E", label: "E" },
            ]}
            idSelected={typeFilter}
            onChange={(id: string) => onTypeFilterChange(id as OplItemType | "all")}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false} style={{ minWidth: 190 }}>
          <EuiSelect
            compressed
            prepend={t("filter.owner", "Verantwortlich")}
            value={ownerFilter}
            options={owners}
            onChange={(e) => onOwnerFilterChange(e.target.value)}
            aria-label={t("filter.owner", "Verantwortlich")}
          />
        </EuiFlexItem>
        <EuiFlexItem />
        <EuiFlexItem grow={false}>
          <EuiButtonGroup
            legend={t("view.meeting", "Meeting-View")}
            buttonSize="compressed"
            options={[
              { id: "meeting", label: t("view.meeting", "Meeting-View") },
              { id: "closed", label: t("view.closed", "Erledigte einblenden") },
              { id: "everything", label: t("view.everything", "Gesamtbestand") },
            ]}
            idSelected={view}
            onChange={(id: string) => onViewChange(id as OplBoardView)}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      {/* --- column header --- */}
      <div className="oplRow__head">
        <span>{t("col.id", "ID")}</span>
        <span>{t("col.type", "Typ")}</span>
        <span>{t("col.due", "Fällig")}</span>
        <span>{t("col.owner", "Verantwortlich")}</span>
        <span>{t("col.status", "Status")}</span>
        <span>{t("col.text", "Text (Log)")}</span>
      </div>

      {/* --- grouped rows --- */}
      <div style={{ overflowY: "auto", minHeight: 0 }}>
        {GROUP_ORDER.filter((key) => groups[key].length > 0).map((key) => (
          <section key={key}>
            <div className="oplGroup">
              <span className={`oplGroup__label${groupMeta[key].mod}`}>
                {groupMeta[key].label}
              </span>
              <span className="oplMono" style={{ color: "var(--opl-sub)" }}>
                {groups[key].length}
              </span>
              <span style={{ color: "var(--opl-sub)" }}>{groupMeta[key].hint}</span>
            </div>

            {groups[key].map((item) => {
              const overdue = isOverdueIn(item, round?.date ?? null);
              const closed = isOplItemClosed(item.status);
              const last = lastLogEntry(item);
              const classes = [
                "oplRow",
                selectedId === item.id ? "oplRow--selected" : "",
                closed ? "oplRow--closed" : "",
                overdue && selectedId !== item.id ? "oplRow--overdue" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  type="button"
                  key={item.id}
                  className={classes}
                  onClick={() => onSelect(item)}
                  aria-current={selectedId === item.id}
                >
                  <span className="oplMono" style={{ fontSize: 12 }}>
                    {item.key}
                  </span>
                  <span
                    className={`oplType oplType--${item.type}`}
                    title={typeLabel(t, item.type)}
                  >
                    {item.type}
                  </span>
                  <span
                    className="oplMono"
                    style={{
                      fontSize: 12,
                      color: overdue ? "var(--opl-danger)" : undefined,
                      fontWeight: overdue ? 600 : undefined,
                    }}
                  >
                    {fmtDate(item.dueDate)}
                  </span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ownerShort(item.owner)}
                  </span>
                  <span style={{ fontSize: 12 }}>
                    {statusLabel(t, item.status)}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span className="oplRow__title">{item.title}</span>
                    {last && (
                      <span className="oplRow__last">
                        <span
                          className="oplMono"
                          style={{ fontSize: 11, color: "var(--opl-sub)", flex: "none" }}
                        >
                          {last.date}
                        </span>
                        <span className="oplRow__lastText">{last.text}</span>
                      </span>
                    )}
                    {item.assignedInAbsence && (
                      <span
                        className="oplStatusChip oplStatusChip--blocked"
                        style={{ display: "inline-block", marginTop: 4 }}
                      >
                        {t("assignedInAbsence", "in Abwesenheit zugewiesen")}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </section>
        ))}

        {GROUP_ORDER.every((key) => groups[key].length === 0) && (
          <div className="oplDashed" style={{ margin: 16 }}>
            {t("empty.points", "Keine Punkte für diesen Filter.")}
          </div>
        )}

        <div style={{ padding: "14px 16px" }}>
          <EuiText size="xs" color="subdued">
            <span className="oplMono">
              {hiddenCount > 0
                ? `${hiddenCount} ${t(
                    "hidden.some",
                    "Punkt(e) ausgeblendet - nicht gelöscht, über „Gesamtbestand“ sichtbar.",
                  )}`
                : `${items.length} · ${t("hidden.none", "Gesamtbestand, nichts gelöscht.")}`}
            </span>
          </EuiText>
        </div>
      </div>
    </main>
  );
};
